import React, { useRef, useEffect, useCallback, useMemo } from "react";
import styles from "./monday-items-list.module.css";
import { MondayItemType } from "frontend/canvas-designer-new/monday-import-items/monday-import-items.types";
import { useMondayImportItemsContext } from "frontend/canvas-designer-new/monday-import-items/monday-import-items-context";
import * as MathUtils from "frontend/utils/math-utils";
import { GroupedItemsList, Group } from "../components/grouped-items-list/grouped-items-list";
import { MondayItem } from "../components/monday-item/monday-item";
import { SubItemsIcon } from "../components/icons/sub-items-icon";
import Checkbox from "frontend/ui-components/design-system/checkbox/checkbox";

interface MondayItemsListProps {
  groups?: { id: string; name: string; color: string; items: MondayItemType[] }[];
  searchValue?: string;
  selectedItems: string[];
  setSelectedItems: (items: string[]) => void;
  currentItem?: MondayItemType | null;
  currentItemColor?: string;
  loadMoreItems: () => void;
  hasMoreItems: boolean;
  onDragEnd: ({ item, pos }: { item: MondayItemType; pos: MathUtils.PointInViewport }) => void;
  itemsLoading: boolean;
  scrollPosition?: number;
  onScroll?: (scrollTop: number) => void;
  onSubItemsClick?: (item: MondayItemType, groupColor: string) => void;
  showSelectAll?: boolean;
}

export const MondayItemsList: React.FC<MondayItemsListProps> = ({
  groups,
  searchValue,
  selectedItems,
  setSelectedItems,
  currentItem,
  currentItemColor,
  loadMoreItems,
  hasMoreItems,
  onDragEnd,
  itemsLoading,
  scrollPosition = 0,
  onScroll,
  onSubItemsClick,
  showSelectAll = false,
}) => {
  const { addBreadcrumb } = useMondayImportItemsContext();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Set scroll position when it changes
  useEffect(() => {
    if (scrollContainerRef.current && scrollPosition !== undefined) {
      scrollContainerRef.current.scrollTop = scrollPosition;
    }
  }, [scrollPosition, currentItem]); // Also depend on currentItem to trigger when view changes

  // Handle scroll events
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !onScroll) return;

    const handleScroll = () => {
      onScroll(container.scrollTop);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [onScroll]);

  const handleItemSelect = (itemId: string, checked: boolean) => {
    if (checked) {
      setSelectedItems([...selectedItems, itemId]);
    } else {
      setSelectedItems(selectedItems.filter((id) => id !== itemId));
    }
  };

  const handleGlobalSelectAll = useCallback(() => {
    const allItemIds = (groups || []).flatMap((group) => group.items.map((item) => item.id));
    const allSelected = allItemIds.length > 0 && allItemIds.every((id) => selectedItems.includes(id));

    if (allSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(allItemIds);
    }
  }, [groups, selectedItems, setSelectedItems]);

  const isAllSelected = useMemo(() => {
    const allItemIds = (groups || []).flatMap((group) => group.items.map((item) => item.id));
    return allItemIds.length > 0 && allItemIds.every((id) => selectedItems.includes(id));
  }, [groups, selectedItems]);

  // Filter items based on search value
  const filterItems = React.useCallback(
    (items: MondayItemType[]): MondayItemType[] => {
      if (!searchValue) return items;

      return items.filter(
        (item) =>
          item.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.subItems?.some((subItem) => subItem?.name.toLowerCase().includes(searchValue.toLowerCase()))
      );
    },
    [searchValue]
  );

  // Prepare groups data based on whether we're viewing sub-items or board items
  const displayGroups = React.useMemo(() => {
    // If we have a currentItem, create a single group for its sub-items
    if (currentItem && currentItem.subItems) {
      const filteredSubItems = filterItems(currentItem.subItems.filter(Boolean) as MondayItemType[]);

      return [
        {
          id: `${currentItem.id}-subitems`,
          name: (
            <div className={styles.subItemsGroupName}>
              <SubItemsIcon color="#1E1E1E" width={13} height={13} />
              <span>{currentItem.name} sub-items</span>
            </div>
          ),
          color: currentItemColor || "#5C6C7C",
          items: filteredSubItems,
          disableCollapse: true,
        },
      ];
    }

    // Otherwise use the regular groups with filtered items
    return (groups || [])
      .map((group) => ({
        ...group,
        items: filterItems(group.items),
      }))
      .filter((group) => group.items.length > 0);
  }, [currentItem, groups, filterItems, currentItemColor]);

  // Determine if we should show the "Load more" button
  const showLoadMore = !currentItem && hasMoreItems && groups && groups.length > 0;

  // Collect all items with their group colors for the drag preview
  const allItemsWithColors = React.useMemo(() => {
    const itemsMap: MondayItemType[] = [];
    const colorsMap: Record<string, string> = {};

    for (const group of displayGroups) {
      for (const item of group.items) {
        itemsMap.push(item);
        colorsMap[item.id] = group.color;
      }
    }

    return { items: itemsMap, colors: colorsMap };
  }, [displayGroups]);

  const renderItem = (item: MondayItemType, groupColor: string) => (
    <MondayItem
      key={item.id}
      item={item}
      groupColor={groupColor}
      searchValue={searchValue}
      isSelected={selectedItems.includes(item.id)}
      onSelect={handleItemSelect}
      onDragEnd={(item, pos) => onDragEnd({ item, pos })}
      onSubItemsClick={(item, groupColor) => {
        addBreadcrumb(item);
        onSubItemsClick?.(item, groupColor);
      }}
      selectedItems={selectedItems}
      allItems={allItemsWithColors.items}
      groupColors={allItemsWithColors.colors}
    />
  );

  const getEmptyMessage = (_group: Group<MondayItemType>) => {
    if (currentItem) {
      return searchValue ? `No sub-items found matching "${searchValue}"` : "No sub-items available";
    }
    return searchValue ? `No items found matching "${searchValue}"` : "No items available";
  };

  return (
    <div ref={scrollContainerRef} className={styles.scrollContainer}>
      {!currentItem && showSelectAll && (
        <div className={styles.selectAllContainer}>
          <span className={styles.selectAllLabel}>Select all</span>
          <Checkbox checked={isAllSelected} onChange={handleGlobalSelectAll} />
        </div>
      )}

      <GroupedItemsList
        groups={displayGroups}
        renderItem={renderItem}
        onLoadMore={showLoadMore ? loadMoreItems : undefined}
        hasMore={showLoadMore}
        isLoading={itemsLoading}
        getEmptyMessage={getEmptyMessage}
      />
      <div className={styles.bottomSpacer} />
    </div>
  );
};
